import { useQuery } from '@tanstack/react-query'
import { Search, Download } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { getDeveloperWorkspacePage } from '@/services/api/developer.service'

export function DeveloperWorkspacesPage() {
  const [search, setSearch] = useState('')
  const { data, isLoading } = useQuery({
    queryKey: ['developer', 'workspaces', 'search', search],
    queryFn: () => getDeveloperWorkspacePage({ search, page: 0, size: 50 }),
  })

  const csvContent = useMemo(() => {
    if (!data?.content?.length) return ''
    const headers = ['workspaceName', 'workspaceId', 'ownerEmail', 'planName', 'subscriptionStatus', 'lockedResourceCount']
    const rows = data.content.map((item) => [
      item.workspaceName,
      item.workspaceId,
      item.ownerEmail ?? '',
      item.planName ?? '',
      item.subscriptionStatus ?? '',
      String(item.lockedResourceCount),
    ])
    return [headers.join(','), ...rows.map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(','))].join('\n')
  }, [data])

  const exportCsv = () => {
    if (!csvContent) return
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const anchor = document.createElement('a')
    anchor.href = url
    anchor.download = 'developer-workspaces.csv'
    anchor.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-bold">Developer Workspaces</h1>
          <p className="mt-1 text-neutral-500">Search, filter, and export workspace inventory.</p>
        </div>
        <Button variant="outline" onClick={exportCsv} disabled={!csvContent}>
          <Download className="mr-2 h-4 w-4" />
          Export CSV
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Workspace Directory</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search workspaces or owner email" className="pl-9" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-neutral-200 text-left text-neutral-500">
                  <th className="px-3 py-2">Workspace</th>
                  <th className="px-3 py-2">Owner</th>
                  <th className="px-3 py-2">Plan</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Locked</th>
                  <th className="px-3 py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-neutral-500">Loading workspaces...</td>
                  </tr>
                ) : data?.content?.length ? (
                  data.content.map((workspace) => (
                    <tr key={workspace.workspaceId} className="border-b border-neutral-100">
                      <td className="px-3 py-3">
                        <div>
                          <p className="font-medium text-neutral-900">{workspace.workspaceName}</p>
                          <p className="text-xs text-neutral-500">{workspace.workspaceId}</p>
                        </div>
                      </td>
                      <td className="px-3 py-3">{workspace.ownerEmail ?? '—'}</td>
                      <td className="px-3 py-3">{workspace.planName ?? 'No plan'}</td>
                      <td className="px-3 py-3">
                        <Badge variant="outline">{workspace.subscriptionStatus ?? 'NONE'}</Badge>
                      </td>
                      <td className="px-3 py-3">{workspace.lockedResourceCount}</td>
                      <td className="px-3 py-3">
                        <Button variant="outline" size="sm" asChild>
                          <Link to={`/developer/workspaces/${workspace.workspaceId}`}>View</Link>
                        </Button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={6} className="px-3 py-8 text-center text-neutral-500">No workspaces found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
